import {useQueryClient} from '@tanstack/react-query'
import {QUERY_KEYS} from '@/queryClient'
import {UserNotification, NotificationsResponse} from '../types'
import {markAsUnseen} from './seenNotifications'

type InfiniteQueryData = {
    pages: NotificationsResponse[]
    pageParams: (number | undefined)[]
}

/**
 * Helper function to update notifications in all cached queries
 */
export const updateNotificationsInCache = (
    queryClient: ReturnType<typeof useQueryClient>,
    updateFn: (notification: UserNotification) => UserNotification
) => {
    // Update all notification queries using setQueriesData - the proper way
    queryClient.setQueriesData<InfiniteQueryData>(
        {queryKey: [QUERY_KEYS.NOTIFICATIONS]},
        (oldData: InfiniteQueryData | undefined) => {
            if (!oldData?.pages?.length) return oldData
            return {
                ...oldData,
                pages: oldData.pages.map(page => ({
                    ...page,
                    data: page.data.map(updateFn)
                }))
            }
        }
    )
}

/**
 * Helper function to add a new notification to all cached queries. Returns
 * whether it merged into an existing entry, so callers can keep an
 * optimistic notification count in sync (see useSocketNotifications).
 *
 * Each notification event is its own row, even when several belong to the
 * same conversation: a notification the user manually marked "da leggere"
 * is a deliberate placeholder and must stay put — a new message on that
 * conversation arrives as a separate, new entry with its own dot, never
 * merged into (or replacing) the placeholder. See NotificationItem for the
 * "manually marked unread" handling this depends on.
 */
export const addNotificationToCache = (
    queryClient: ReturnType<typeof useQueryClient>,
    newNotification: UserNotification
): {hadExistingEntry: boolean} => {
    let hadExistingEntryResult = false

    // Update all notification queries using setQueriesData - the proper way
    queryClient.setQueriesData<InfiniteQueryData>(
        {queryKey: [QUERY_KEYS.NOTIFICATIONS]},
        (oldData: InfiniteQueryData | undefined) => {
            if (!oldData?.pages?.length) return oldData

            // A socket push can reference a notification already present in the cache
            // (e.g. an optimistic stub added by an earlier push, or the record already
            // loaded via REST) — match on either id to avoid rendering it twice.
            const isSameNotification = (notification: UserNotification) =>
                notification.id === newNotification.id ||
                notification.notification.id === newNotification.notification.id
            const hadExistingEntry = oldData.pages.some(page => page.data.some(isSameNotification))
            hadExistingEntryResult = hadExistingEntry
            if (hadExistingEntry) {
                // The same notification id was already in the cache (e.g. an optimistic
                // stub reconciling with the REST-confirmed record, or a duplicate socket
                // delivery) — re-surface its unread dot rather than leaving it stale.
                markAsUnseen(newNotification.id)
            }

            return {
                ...oldData,
                pages: oldData.pages.map((page, index) => {
                    const data = page.data.filter(notification => !isSameNotification(notification))
                    // Only add to the first page
                    if (index === 0) {
                        // Socket delivery order isn't guaranteed to match sentAt order
                        // (e.g. two messages a second apart can arrive back-to-back) —
                        // re-sort by sentAt instead of assuming the newest push belongs
                        // at the very top.
                        const sortedData = [newNotification, ...data].sort(
                            (a, b) =>
                                new Date(b.notification.sentAt).getTime() - new Date(a.notification.sentAt).getTime()
                        )
                        return {
                            data: sortedData,
                            meta: {
                                ...page.meta,
                                total: (page.meta.total || 0) + (hadExistingEntry ? 0 : 1)
                            }
                        }
                    }
                    return data.length === page.data.length ? page : {...page, data}
                })
            }
        }
    )

    return {hadExistingEntry: hadExistingEntryResult}
}
